/* eslint-disable @typescript-eslint/naming-convention */
import log from 'loglevel';
import { IAddress } from 'types/address';
import { getDetails } from 'use-places-autocomplete';

export const handlePlacesAutocompleteSuggestionSelect = async (suggestion: google.maps.places.AutocompletePrediction): Promise<IAddress | null> => {
  log.debug('suggestion', suggestion);
  const details = await getDetails({ placeId: suggestion.place_id });
  if (typeof details === 'string' || !details.address_components || !details.geometry || !details.geometry.location) {
    console.error('Unknown place details:', details);
    return null;
  }
  const { address_components, geometry: { location: { lat, lng } } } = details;

  let streetNumber = '';
  let route = '';

  let address2 = '';

  let city = '';
  let state = '';
  let zipcode = '';

  log.debug(address_components);

  address_components.forEach(({ long_name, short_name, types }) => {
    if (types.includes('street_number')) {
      streetNumber = short_name;
    } else if (types.includes('route')) {
      route = short_name;
    } else if (types.includes('subpremise')) {
      address2 = short_name;
    } else if (!city && types.includes('political') && (types.includes('neighborhood') || types.includes('locality'))) {
      // Sometimes we get two political neighborhood/locality type results, so pick whichever one
      // is in the "official" description shown when searching. This can be reproduced with "9525 W Russell Rd C120 Las Vegas NV 89148".

      // Also, pick either the long or short name, depending on which is in the address string Google gives (the "official" description)
      if (suggestion.description.includes(short_name)) {
        city = short_name;
      } else if (suggestion.description.includes(long_name)) {
        city = long_name;
      }
    } else if (types.includes('administrative_area_level_1')) {
      state = short_name;
    } else if (types.includes('postal_code')) {
      zipcode = short_name;
    }
  });

  // Edge case where sometimes the city name from the Google description string is not
  // returned as an address part. In this case fallback to the first 'political' name
  //
  // This can be reproduced with "7223 Silverwood Dr, Eastvale, CA 92880". It gives
  // the city "Corona" which is not in the official description.
  if (!city) {
    const firstPolitical = address_components.find(({ types }) => types.includes('political'));

    if (!firstPolitical) {
      // Further edge case here. Throw an error so we can fix it.
      throw new Error(`Unable to find fallback city for address search string ${suggestion.description}`);
    }

    city = firstPolitical.short_name;
  }

  // Combine the street number and street to create address1
  const address1 = `${streetNumber}${streetNumber ? ' ' : ''}${route}`;

  return {
    address1,
    address2,
    city,
    state,
    zipcode,
    location: { lat: lat(), lng: lng() },
  };
};

export const getFirstPrediction = async (input: string): Promise<IAddress | null> => {
  const autocompleteService = new google.maps.places.AutocompleteService();

  const { predictions } = await autocompleteService.getPlacePredictions({
    input,
    types: ['address'],
    componentRestrictions: { country: 'us' },
  });

  if (!predictions?.length) {
    return null;
  }

  const first = predictions[0];

  return handlePlacesAutocompleteSuggestionSelect(first);
};
